import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AccessibleLoader } from '@/components/shared/AccessibleLoader';
import { fetchPublicDeck } from '@/utils/api-pb';

/**
 * A shared deck, as an investor sees it.
 *
 * Outside the app shell entirely: no sidebar, no header, no login prompt — whoever
 * opens this link has no account and should not be asked for one.
 *
 * The deck is shown in a sandboxed iframe rather than injected into this page. The
 * document comes from a renderer, and if a bug in one ever let markup through, that
 * markup would run next to the PocketBase session of whoever opened the link — which,
 * for a deck the owner opens to check their own link, is their own session. An opaque
 * origin (no `allow-same-origin`) is what keeps the two apart.
 */
export default function PublicDeckPage() {
  const { buildId = '' } = useParams();
  const [params] = useSearchParams();
  const { t } = useTranslation('builder');

  const [html, setHtml] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const token = params.get('token') || '';

    async function load() {
      try {
        const deck = await fetchPublicDeck(buildId, token);
        if (cancelled) return;
        setHtml(deck.html);
        if (deck.name) document.title = deck.name;
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : t('deck.public.error'));
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [buildId, params, t]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-[8px] bg-[#0d0d0d] px-[24px] text-center">
        <h1 className="font-manrope text-[20px] font-bold text-white">{t('deck.public.error')}</h1>
        <p className="max-w-[420px] font-segoe text-[15px] text-white/60">
          {t('deck.public.errorHint')}
        </p>
      </div>
    );
  }

  if (!html) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0d0d0d]">
        <AccessibleLoader label={t('loading', { ns: 'common' })} />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-[#0d0d0d]">
      <div className="flex min-h-0 flex-1 items-center justify-center p-[16px]">
        <div className="aspect-video w-full max-w-[1280px] overflow-hidden rounded-[16px] bg-white shadow-lg">
          <iframe
            title="deck"
            srcDoc={html}
            sandbox="allow-scripts"
            className="block h-full w-full border-0"
          />
        </div>
      </div>
      <p className="pb-[16px] text-center font-segoe text-[13px] text-white/50">
        {t('deck.public.madeWith')}
      </p>
    </div>
  );
}
